<template>
  <LoadingScreen loadingText="X 연동 처리 중..." />
</template>

<script>
import LoadingScreen from "@/components/LoadingScreen.vue";
import apiClient from "@/utils/api";
import { updateUserFields } from "@/utils/write/user";
import { useUserStore } from "@/store/user";
import { trackActivity, ACTIVITY_TYPES } from "@/utils/activityTracker";

export default {
  name: "XCallback",
  components: { LoadingScreen },
  data() {
    return {
      userStore: useUserStore(),
    };
  },
  async created() {
    try {
      const urlParams = new URLSearchParams(window.location.search);

      if (urlParams.has("code") && urlParams.has("state")) {
        const code = urlParams.get("code");
        const state = urlParams.get("state");
        await this.handleXCallback(code, state);
      } else if (urlParams.has("error")) {
        const error = urlParams.get("error");
        const errorDescription = urlParams.get("error_description");
        this.sendErrorMessage(
          `${error}: ${errorDescription || "알 수 없는 오류"}`
        );
      } else {
        this.sendErrorMessage("잘못된 콜백 요청입니다.");
      }
    } catch (error) {
      this.sendErrorMessage(`초기화 오류: ${error.message}`);
    }
  },
  methods: {
    async handleXCallback(code, state) {
      try {
        // 쿠키에서 OAuth 데이터 읽기
        const storedState = this.getCookie("x_state");
        const storedCodeVerifier = this.getCookie("x_code_verifier");
        const authType = this.getCookie("x_auth_type");
        const isProfileConnect = authType === "profile";

        // OAuth 데이터 검증
        if (!storedState) {
          throw new Error("저장된 State를 찾을 수 없습니다.");
        }

        if (storedState !== state) {
          throw new Error(
            "State 매개변수가 일치하지 않습니다. 보안상 연동을 중단합니다."
          );
        }

        if (!storedCodeVerifier) {
          throw new Error("Code verifier를 찾을 수 없습니다.");
        }

        if (!isProfileConnect) {
          throw new Error("프로필 연동 요청이 아닙니다.");
        }

        const tokens = await this.exchangeCodeForTokens(
          code,
          storedCodeVerifier
        );
        const userInfo = await this.getUserInfo(tokens.access_token);
        await this.updateUserProfile(userInfo.data);

        // 쿠키 정리
        document.cookie =
          "x_state=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie =
          "x_code_verifier=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie =
          "x_auth_type=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

        // 모바일 감지
        const isMobile =
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
          );

        if (isMobile) {
          // 모바일에서는 sendSuccessMessage에서 리다이렉트 처리
          this.sendSuccessMessage("X에서 프로필 정보를 성공적으로 가져왔습니다!");
        } else {
          // 데스크톱에서는 메시지 전송 후 창 닫기
          this.sendSuccessMessage("X에서 프로필 정보를 성공적으로 가져왔습니다!");
          setTimeout(() => this.closeWindow(), 2000);
        }
      } catch (error) {
        // 모바일 감지
        const isMobile =
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
          );

        if (isMobile) {
          // 모바일에서는 sendErrorMessage에서 리다이렉트 처리
          this.sendErrorMessage(`X 연동 실패: ${error.message}`);
        } else {
          // 데스크톱에서는 메시지 전송 후 창 닫기
          this.sendErrorMessage(`X 연동 실패: ${error.message}`);
          setTimeout(() => this.closeWindow(), 2000);
        }
      }
    },

    async exchangeCodeForTokens(code, codeVerifier) {
      const clientId = process.env.VUE_APP_X_CLIENT_ID;
      const redirectUri = `${process.env.VUE_APP_BASE_URL}/x-callback`;

      const tokenParams = new URLSearchParams({
        grant_type: "authorization_code",
        code: code,
        client_id: clientId,
        redirect_uri: redirectUri,
        code_verifier: codeVerifier,
      });

      const response = await fetch("/api/x/2/oauth2/token", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: tokenParams.toString(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: "응답 파싱 실패", raw_response: errorText };
        }
        throw new Error(
          `토큰 교환 실패 (${response.status}): ${errorData.error_description || errorData.error || "알 수 없는 오류"
          }`
        );
      }

      return await response.json();
    },

    async getUserInfo(accessToken) {
      const url = new URL("/api/x/2/users/me", window.location.origin);
      url.searchParams.append(
        "user.fields",
        "id,name,username,description,profile_image_url,public_metrics,verified"
      );

      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: "응답 파싱 실패", raw_response: errorText };
        }
        throw new Error(
          `사용자 정보 조회 실패 (${response.status}): ${errorData.error_description || errorData.error || "알 수 없는 오류"
          }`
        );
      }

      return await response.json();
    },

    async updateUserProfile(userData) {
      const updateData = {};

      // 프로필 이미지 업데이트
      if (userData.profile_image_url) {
        try {
          const highResImageUrl = userData.profile_image_url.replace(
            "_normal",
            "_400x400"
          );
          await this.uploadProfileImage(highResImageUrl);
        } catch (error) {
          // 프로필 이미지 업데이트 실패는 연동 자체를 실패로 처리하지 않음
        }
      }

      // 닉네임 업데이트 (최대 10글자)
      if (userData.name) {
        const limitedNickname = Array.from(String(userData.name)).slice(0, 10).join("");
        if (limitedNickname && limitedNickname !== this.userStore.nickname) {
          updateData.nickname = limitedNickname;
          this.userStore.nickname = limitedNickname;
        }
      }

      // X 프로필 링크 업데이트 (username 또는 id 사용)
      if (userData.username || userData.id) {
        const xUserId = userData.username || userData.id;
        updateData.detailLink = `https://x.com/${xUserId}`;
      }

      // Firestore 업데이트 (한 번에 처리)
      if (Object.keys(updateData).length > 0) {
        try {
          await updateUserFields(this.userStore.email, updateData);
        } catch (error) {
          // 프로필 업데이트 실패는 연동 자체를 실패로 처리하지 않음
        }
      }

      // X 정보 가져오기 활동 기록
      trackActivity(ACTIVITY_TYPES.X_PROFILE_FETCH, {
        metadata: {
          username: userData.username,
          hasProfileImage: !!userData.profile_image_url,
        },
      });
    },

    async uploadProfileImage(imageUrl) {
      // 백엔드에서 외부 URL 이미지 다운로드 및 Storage 업로드 처리 (CORS 우회)
      const response = await apiClient.post(
        `/api/user/${encodeURIComponent(this.userStore.email)}/profile-image-url`,
        { imageUrl }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || "프로필 이미지 업로드 실패");
      }
    },

    sendSuccessMessage(message) {
      // 모바일 감지
      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

      if (isMobile) {
        // 모바일에서는 sessionStorage에 저장하고 프로필 페이지로 리다이렉트
        sessionStorage.setItem("x_connect_status", "success");
        sessionStorage.setItem("x_connect_message", message);
        window.location.href = `${window.location.origin}/profile`;
      } else if (window.opener) {
        // 데스크톱에서는 window.opener로 메시지 전송
        window.opener.postMessage(
          {
            type: "X_CONNECT_SUCCESS",
            message: message,
          },
          window.location.origin
        );
      }
    },

    sendErrorMessage(message) {
      // 모바일 감지
      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

      if (isMobile) {
        // 모바일에서는 sessionStorage에 저장하고 프로필 페이지로 리다이렉트
        sessionStorage.setItem("x_connect_status", "error");
        sessionStorage.setItem("x_connect_message", message);
        window.location.href = `${window.location.origin}/profile`;
      } else if (window.opener) {
        // 데스크톱에서는 window.opener로 메시지 전송
        window.opener.postMessage(
          {
            type: "X_CONNECT_ERROR",
            message: message,
          },
          window.location.origin
        );
        // 에러 메시지 전달 후 충분한 딜레이
        setTimeout(() => this.closeWindow(), 2000);
      }
    },

    getCookie(name) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) {
        return decodeURIComponent(parts.pop().split(";").shift());
      }
      return null;
    },

    closeWindow() {
      try {
        // 모바일 환경에서는 부모 창 존재 여부와 관계없이 현재 창을 닫기 시도
        const isMobile =
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
          );

        if (isMobile) {
          // 모바일에서는 먼저 부모 창으로 메시지 전송 후 창 닫기
          if (window.opener && !window.opener.closed) {
            // 부모 창이 여전히 열려 있는 경우에만 메시지 전송
            try {
              window.opener.focus(); // 부모 창에 포커스 이동
            } catch (e) {
              // 포커스 이동 실패 시 무시
            }
          }

          // 모바일에서는 약간의 지연 후 창 닫기
          setTimeout(() => {
            try {
              window.close();
              // 창이 닫히지 않으면 부모 창으로 이동 시도
              setTimeout(() => {
                if (window.opener && !window.opener.closed) {
                  window.opener.location.reload(); // 부모 창 새로고침
                  window.close();
                } else {
                  // 마지막 수단으로 프로필 페이지로 이동
                  window.location.href = `${window.location.origin}/profile`;
                }
              }, 100);
            } catch (e) {
              // 창 닫기 실패 시 프로필 페이지로 이동
              window.location.href = `${window.location.origin}/profile`;
            }
          }, 200);
        } else {
          // 데스크톱에서는 기존 로직 사용
          if (window.opener) {
            window.close();
          } else {
            this.$router.push("/profile");
          }
        }
      } catch (error) {
        // 에러 발생 시 강제로 프로필 페이지로 이동
        window.location.href = `${window.location.origin}/profile`;
      }
    },
  },
};
</script>

<style scoped>
/* LoadingScreen 컴포넌트 사용으로 별도 스타일 불필요 */
</style>
